<template>
  <div>
    <AdminNav />
    <main class="page">
      <div class="page-header">
        <div class="page-heading">
          <h2 class="page-title">Facturation</h2>
          <p class="subtitle">Brouillons, PDF et statuts des factures commerçants.</p>
        </div>
        <button class="btn btn-primary btn-new" @click="nouvelleFacture">Nouvelle facture</button>
      </div>

      <p class="success-message" v-if="message">{{ message }}</p>
      <p class="error-message" v-if="erreur">{{ erreur }}</p>

      <div class="layout">
        <section class="card list-card">
          <div class="card-title">Factures</div>
          <div class="empty" v-if="!factures.length">Aucune facture.</div>
          <button
            v-for="facture in factures"
            :key="facture.id"
            class="invoice-row"
            :class="{ active: facture.id === form.id }"
            @click="chargerFacture(facture.id)"
          >
            <span>
              <strong>{{ facture.numero }}</strong>
              <small>{{ facture.client_nom || facture.nom_enseigne || 'Client' }}</small>
            </span>
            <span>
              <em>{{ formatMoney(facture.total_ttc) }}</em>
              <small class="badge" :class="'badge-' + facture.statut">{{ libelleStatut(facture.statut) }}</small>
            </span>
          </button>
        </section>

        <section class="card form-card">
          <div class="form-head">
            <div>
              <div class="card-title">{{ form.id ? form.numero : 'Nouvelle facture' }}</div>
              <p class="subtitle">Les totaux sont recalculés côté serveur à l’enregistrement.</p>
            </div>
            <span class="badge" :class="'badge-' + form.statut">{{ libelleStatut(form.statut) }}</span>
          </div>

          <div class="grid">
            <div class="form-group">
              <label class="label">Commerçant</label>
              <select v-model="form.commercant_id" class="input" @change="syncClientNom">
                <option value="">Sélectionner</option>
                <option v-for="c in commercants" :key="c.id" :value="c.id">{{ c.nom_enseigne }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="label">Nom client</label>
              <input v-model="form.client_nom" class="input" />
            </div>
            <div class="form-group">
              <label class="label">Date émission</label>
              <input v-model="form.date_emission" class="input" type="date" />
            </div>
            <div class="form-group">
              <label class="label">Date échéance</label>
              <input v-model="form.date_echeance" class="input" type="date" />
            </div>
            <div class="form-group">
              <label class="label">Email client</label>
              <input v-model="form.client_email" class="input" type="email" />
            </div>
            <div class="form-group">
              <label class="label">TVA applicable</label>
              <select v-model="form.tva_applicable" class="input" @change="normaliserTva">
                <option :value="false">Non</option>
                <option :value="true">Oui</option>
              </select>
            </div>
            <div class="form-group">
              <label class="label">Mode de règlement</label>
              <select v-model="form.mode_reglement" class="input">
                <option>À définir</option>
                <option>Virement bancaire</option>
                <option>Espèces</option>
                <option>Carte bancaire</option>
                <option>Chèque</option>
                <option>Prélèvement</option>
                <option>Autre</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="label">Adresse client</label>
            <textarea v-model="form.client_adresse" class="input textarea"></textarea>
          </div>

          <div class="form-group">
            <label class="label">Mention TVA</label>
            <input v-model="form.mention_tva" class="input" />
          </div>

          <div class="lines-head">
            <div class="card-title">Lignes</div>
            <button class="btn btn-secondary small" @click="ajouterLigne">Ajouter une ligne</button>
          </div>

          <div class="lines">
            <div class="line-grid line-header">
              <span>Description</span>
              <span>Qté</span>
              <span>PU HT</span>
              <span>TVA %</span>
              <span>Total TTC</span>
              <span></span>
            </div>
            <div class="line-grid" v-for="(ligne, index) in form.lignes" :key="index">
              <label class="line-field line-description">
                <span>Description</span>
                <input v-model="ligne.description" class="input" placeholder="Prestation" />
              </label>
              <label class="line-field">
                <span>Qté</span>
                <input v-model.number="ligne.quantite" class="input" type="number" min="0" step="0.01" />
              </label>
              <label class="line-field">
                <span>PU HT</span>
                <input v-model.number="ligne.prix_unitaire_ht" class="input" type="number" min="0" step="0.01" />
              </label>
              <label class="line-field">
                <span>TVA %</span>
                <input v-model.number="ligne.taux_tva" class="input" type="number" min="0" step="0.01" :disabled="!form.tva_applicable" />
              </label>
              <div class="line-total"><span>Total TTC</span>{{ formatMoney(totalLigne(ligne).total_ttc) }}</div>
              <button class="btn-icon" @click="retirerLigne(index)" :disabled="form.lignes.length === 1" aria-label="Retirer la ligne">×</button>
            </div>
          </div>

          <div class="totals">
            <span>HT <strong>{{ formatMoney(totaux.total_ht) }}</strong></span>
            <span>TVA <strong>{{ formatMoney(totaux.total_tva) }}</strong></span>
            <span>TTC <strong>{{ formatMoney(totaux.total_ttc) }}</strong></span>
          </div>

          <div class="form-group">
            <label class="label">Notes</label>
            <textarea v-model="form.notes" class="input textarea"></textarea>
          </div>

          <div class="actions">
            <button class="btn btn-primary" @click="enregistrer" :disabled="loading">{{ loading ? 'Enregistrement...' : 'Enregistrer brouillon' }}</button>
            <button class="btn btn-secondary" @click="genererPdf" :disabled="!form.id || loading">Générer PDF</button>
            <button class="btn btn-secondary" @click="telechargerPdf" :disabled="!form.id || !form.fichier_pdf || loading">Télécharger PDF</button>
            <button class="btn btn-secondary" @click="changerStatut('envoyee')" :disabled="!form.id || loading">Marquer envoyée</button>
            <button class="btn btn-secondary" @click="changerStatut('payee')" :disabled="!form.id || loading">Marquer payée</button>
            <button class="btn btn-warning" @click="changerStatut('annulee')" :disabled="!form.id || loading">Annuler</button>
            <button class="btn btn-danger" @click="supprimer" :disabled="!form.id || form.statut !== 'brouillon' || loading">Supprimer brouillon</button>
          </div>

          <section class="linked-docs" v-if="form.id">
            <div class="lines-head">
              <div class="card-title">Justificatifs liés</div>
              <button class="btn btn-secondary small" @click="ajouterJustificatifFacture">Ajouter justificatif</button>
            </div>
            <div class="empty" v-if="!justificatifsLies.length">Aucun justificatif lié.</div>
            <div class="doc-row" v-for="doc in justificatifsLies" :key="doc.id">
              <span>
                <strong>{{ doc.libelle }}</strong>
                <small>{{ doc.categorie || 'Sans catégorie' }} · {{ formatDateDisplay(doc.date_document) }}</small>
              </span>
              <button class="btn btn-secondary small" @click="telechargerJustificatif(doc)" :disabled="!doc.has_file">Télécharger</button>
            </div>
          </section>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import AdminNav from '../../components/AdminNav.vue'
import adminApi from '../../adminApi'

const mentionTvaDefaut = 'TVA non applicable, art. 293 B du CGI'

export default {
  name: 'AdminFacturation',
  components: { AdminNav },
  data() {
    return {
      factures: [],
      commercants: [],
      loading: false,
      erreur: '',
      message: '',
      justificatifsLies: [],
      form: this.factureVide()
    }
  },
  computed: {
    totaux() {
      return this.form.lignes.reduce((total, ligne) => {
        const calc = this.totalLigne(ligne)
        return {
          total_ht: this.round(total.total_ht + calc.total_ht),
          total_tva: this.round(total.total_tva + calc.total_tva),
          total_ttc: this.round(total.total_ttc + calc.total_ttc)
        }
      }, { total_ht: 0, total_tva: 0, total_ttc: 0 })
    }
  },
  methods: {
    factureVide() {
      const today = new Date().toISOString().slice(0, 10)
      return {
        id: '',
        numero: 'Brouillon',
        statut: 'brouillon',
        commercant_id: '',
        date_emission: today,
        date_echeance: '',
        client_nom: '',
        client_adresse: '',
        client_email: '',
        tva_applicable: false,
        mode_reglement: 'À définir',
        mention_tva: mentionTvaDefaut,
        notes: '',
        fichier_pdf: '',
        lignes: [{ description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 0 }]
      }
    },
    round(value) { return Math.round(Number(value || 0) * 100) / 100 },
    formatMoney(value) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0))
    },
    formatDateInput(value) {
      return value ? String(value).slice(0, 10) : ''
    },
    formatDateDisplay(value) {
      return value ? new Date(value).toLocaleDateString('fr-FR') : 'Sans date'
    },
    libelleStatut(statut) {
      return { brouillon: 'Brouillon', envoyee: 'Envoyée', partiellement_payee: 'Partielle', payee: 'Payée', annulee: 'Annulée' }[statut] || statut
    },
    totalLigne(ligne) {
      const quantite = Number(ligne.quantite || 0)
      const prix = Number(ligne.prix_unitaire_ht || 0)
      const taux = this.form.tva_applicable ? Number(ligne.taux_tva || 0) : 0
      const totalHt = this.round(quantite * prix)
      const totalTva = this.round(totalHt * taux / 100)
      return { total_ht: totalHt, total_tva: totalTva, total_ttc: this.round(totalHt + totalTva) }
    },
    payload() {
      return {
        ...this.form,
        lignes: this.form.lignes.map((ligne, index) => ({ ...ligne, ordre: index }))
      }
    },
    async chargerInitial() {
      await Promise.all([this.chargerCommercants(), this.chargerFactures()])
    },
    async chargerCommercants() {
      const res = await adminApi.get('/commercants')
      this.commercants = res.data || []
    },
    async chargerFactures() {
      const res = await adminApi.get('/factures')
      this.factures = res.data || []
    },
    async chargerFacture(id) {
      this.erreur = ''
      const res = await adminApi.get(`/factures/${id}`)
      this.form = {
        ...this.factureVide(),
        ...res.data,
        date_emission: this.formatDateInput(res.data.date_emission),
        date_echeance: this.formatDateInput(res.data.date_echeance),
        mode_reglement: res.data.mode_reglement || 'À définir',
        lignes: (res.data.lignes || []).map(ligne => ({
          description: ligne.description,
          quantite: Number(ligne.quantite),
          prix_unitaire_ht: Number(ligne.prix_unitaire_ht),
          taux_tva: Number(ligne.taux_tva)
        }))
      }
      await this.chargerJustificatifsFacture()
    },
    nouvelleFacture() {
      this.form = this.factureVide()
      this.justificatifsLies = []
      this.message = ''
      this.erreur = ''
    },
    syncClientNom() {
      if (this.form.client_nom) return
      const commercant = this.commercants.find(c => c.id === this.form.commercant_id)
      if (commercant) this.form.client_nom = commercant.nom_enseigne
    },
    normaliserTva() {
      if (!this.form.tva_applicable) {
        this.form.mention_tva = this.form.mention_tva || mentionTvaDefaut
        this.form.lignes = this.form.lignes.map(ligne => ({ ...ligne, taux_tva: 0 }))
      }
    },
    ajouterLigne() {
      this.form.lignes.push({ description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: this.form.tva_applicable ? 20 : 0 })
    },
    retirerLigne(index) {
      if (this.form.lignes.length > 1) this.form.lignes.splice(index, 1)
    },
    async enregistrer() {
      this.loading = true
      this.erreur = ''
      this.message = ''
      try {
        const res = this.form.id
          ? await adminApi.put(`/factures/${this.form.id}`, this.payload())
          : await adminApi.post('/factures', this.payload())
        await this.chargerFactures()
        await this.chargerFacture(res.data.id)
        this.message = 'Facture enregistrée.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible d’enregistrer la facture.'
      } finally {
        this.loading = false
      }
    },
    async genererPdf() {
      this.loading = true
      this.erreur = ''
      this.message = ''
      try {
        await adminApi.post(`/factures/${this.form.id}/generate-pdf`)
        await this.chargerFacture(this.form.id)
        await this.chargerFactures()
        this.message = 'PDF généré.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de générer le PDF.'
      } finally {
        this.loading = false
      }
    },
    async telechargerPdf() {
      try {
        const res = await adminApi.get(`/factures/${this.form.id}/pdf`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${this.form.numero}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de télécharger le PDF.'
      }
    },
    async chargerJustificatifsFacture() {
      if (!this.form.id) {
        this.justificatifsLies = []
        return
      }
      const res = await adminApi.get('/justificatifs', { params: { facture_id: this.form.id, statut: 'actif' } })
      this.justificatifsLies = res.data || []
    },
    ajouterJustificatifFacture() {
      this.$router.push({
        path: '/admin/justificatifs',
        query: {
          facture_id: this.form.id,
          type: 'vente',
          categorie: 'Facture client',
          libelle: this.form.numero
        }
      })
    },
    async telechargerJustificatif(doc) {
      try {
        const res = await adminApi.get(`/justificatifs/${doc.id}/download`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.fichier_nom_original || doc.libelle || 'justificatif')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de télécharger le justificatif.'
      }
    },
    async changerStatut(statut) {
      this.loading = true
      this.erreur = ''
      try {
        await adminApi.patch(`/factures/${this.form.id}/statut`, { statut })
        await this.chargerFacture(this.form.id)
        await this.chargerFactures()
        await this.chargerJustificatifsFacture()
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de changer le statut.'
      } finally {
        this.loading = false
      }
    },
    async supprimer() {
      if (!confirm('Supprimer cette facture brouillon ?')) return
      this.loading = true
      this.erreur = ''
      try {
        await adminApi.delete(`/factures/${this.form.id}`)
        await this.chargerFactures()
        this.nouvelleFacture()
        this.message = 'Facture supprimée.'
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de supprimer la facture.'
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.chargerInitial().catch(err => {
      this.erreur = err.response?.data?.error || 'Impossible de charger la facturation.'
    })
  }
}
</script>

<style scoped>
.page {
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
}
.page-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
}
.page-heading { min-width: 0; }
.page-title { font-size: 22px; font-weight: 700; margin: 0 0 4px; color: #1a202c; }
.subtitle { margin: 0; color: #718096; font-size: 13px; line-height: 1.4; }
.layout { display: grid; grid-template-columns: 1fr; gap: 16px; align-items: start; }
.card { background: white; border-radius: 8px; padding: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); min-width: 0; }
.card-title { font-size: 15px; font-weight: 700; color: #2d3748; }
.list-card { display: flex; flex-direction: column; gap: 8px; }
.invoice-row {
  width: 100%;
  border: 1px solid #edf2f7;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  text-align: left;
  cursor: pointer;
}
.invoice-row.active, .invoice-row:hover { border-color: #2d3748; background: #f7fafc; }
.invoice-row span { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.invoice-row small { color: #718096; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.invoice-row em { font-style: normal; font-weight: 700; text-align: right; white-space: nowrap; }
.form-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 18px; }
.grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
.form-group { margin-bottom: 14px; min-width: 0; }
.label { display: block; font-size: 13px; font-weight: 600; color: #4a5568; margin-bottom: 5px; }
.input {
  width: 100%;
  min-height: 40px;
  padding: 9px 11px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
}
.textarea { min-height: 76px; resize: vertical; }
.lines-head { display: flex; flex-direction: column; gap: 10px; margin: 12px 0; }
.lines { display: grid; gap: 10px; margin-bottom: 14px; }
.line-grid {
  border: 1px solid #edf2f7;
  border-radius: 8px;
  padding: 10px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.line-header { display: none; }
.line-field { display: grid; gap: 5px; min-width: 0; }
.line-field span,
.line-total span { color: #718096; font-size: 12px; font-weight: 700; }
.line-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.totals {
  display: grid;
  gap: 8px;
  padding: 14px;
  background: #f7fafc;
  border-radius: 8px;
  margin-bottom: 14px;
}
.totals span { display: flex; justify-content: space-between; gap: 12px; color: #4a5568; }
.totals strong { color: #1a202c; }
.actions { display: grid; grid-template-columns: 1fr; gap: 10px; }
.linked-docs { margin-top: 18px; }
.doc-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 10px;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  margin-bottom: 8px;
  box-sizing: border-box;
}
.doc-row span { display: grid; gap: 4px; min-width: 0; }
.doc-row small { color: #718096; font-size: 12px; font-weight: 700; }
.btn { min-height: 40px; padding: 10px 16px; border-radius: 8px; border: none; font-size: 14px; font-weight: 600; cursor: pointer; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: #2d3748; color: white; }
.btn-secondary { background: #edf2f7; color: #4a5568; }
.btn-warning { background: #fff3cd; color: #744210; }
.btn-danger { background: #fed7d7; color: #742a2a; }
.small { padding: 8px 12px; }
.btn-icon {
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  width: 100%;
  height: 38px;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}
.badge { display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.badge-brouillon { background: #edf2f7; color: #4a5568; }
.badge-envoyee { background: #bee3f8; color: #2a4365; }
.badge-partiellement_payee { background: #fff3cd; color: #744210; }
.badge-payee { background: #c6f6d5; color: #22543d; }
.badge-annulee { background: #fed7d7; color: #742a2a; }
.success-message { color: #22543d; background: #c6f6d5; padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; }
.error-message { color: #742a2a; background: #fed7d7; padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; }
.empty { color: #718096; font-size: 14px; padding: 12px 0; }

@media (min-width: 640px) {
  .page { padding: 22px; }
  .page-header { flex-direction: row; align-items: flex-start; justify-content: space-between; }
  .btn-new { width: auto; flex: 0 0 auto; }
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .lines-head { flex-direction: row; align-items: center; justify-content: space-between; }
  .actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .doc-row { grid-template-columns: minmax(0, 1fr) auto; align-items: center; }
  .totals { display: flex; justify-content: flex-end; gap: 18px; }
  .totals span { display: inline; }
  .totals strong { margin-left: 6px; }
}

@media (min-width: 980px) {
  .page { padding: 24px; }
  .layout { grid-template-columns: minmax(280px, 340px) minmax(0, 1fr); gap: 18px; }
  .card { padding: 18px; }
  .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .actions { display: flex; flex-wrap: wrap; }
  .lines {
    display: block;
    border: 1px solid #edf2f7;
    border-radius: 8px;
    overflow-x: auto;
  }
  .line-grid {
    min-width: 720px;
    border: 0;
    border-radius: 0;
    border-top: 1px solid #edf2f7;
    grid-template-columns: minmax(220px, 1fr) 86px 110px 86px 110px 42px;
    gap: 8px;
    align-items: center;
    padding: 8px;
  }
  .line-grid:first-child { border-top: none; }
  .line-header {
    display: grid;
    background: #f7fafc;
    color: #718096;
    font-size: 12px;
    font-weight: 700;
  }
  .line-field span,
  .line-total span { display: none; }
  .line-total { justify-content: flex-end; text-align: right; }
  .btn-icon { width: 34px; height: 34px; }
}

@media (max-width: 420px) {
  .page { padding: 12px; }
  .card { padding: 14px; }
  .page-title { font-size: 20px; }
  .form-head { flex-direction: column; }
  .invoice-row { grid-template-columns: 1fr; }
  .invoice-row em { text-align: left; }
}
</style>
